import {Injectable, OnDestroy} from '@angular/core';
import {SwUpdate, VersionEvent} from '@angular/service-worker';
import {BehaviorSubject, interval, Observable, Subscription} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ServiceWorkerService implements OnDestroy {

  private swSubscriptions: Subscription[] = [];
  private eventSubject = new BehaviorSubject<ServiceWorkerEvent>('sw_initialized');
  event$: Observable<ServiceWorkerEvent> = this.eventSubject.asObservable();

  constructor(private swUpdate: SwUpdate) {
    this.swSubscriptions.push(this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      if (event.type === 'NO_NEW_VERSION_DETECTED') {
        this.eventSubject.next('no_update_available');
      }
      if (event.type === 'VERSION_READY') {
        this.eventSubject.next('update_available');
      }
      if (event.type === 'VERSION_INSTALLATION_FAILED') {
        this.eventSubject.next('update_failed');
      }
    }));
    this.swSubscriptions.push(this.swUpdate.unrecoverable.subscribe(event => {
      console.error(event);
      this.eventSubject.next('sw_error');
    }));
    if (this.isServiceWorkerEnabled()) {
      this.swSubscriptions.push(interval(60 * 60 * 1000).subscribe(() => this.checkForUpdate()));
    }
  }

  ngOnDestroy(): void {
    this.swSubscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public async checkForUpdate() {
    return this.swUpdate.checkForUpdate();
  }

  public isServiceWorkerEnabled(): boolean {
    return this.swUpdate.isEnabled;
  }

  /**
   * Force reset the PWA by unregistering service workers and clearing all caches.
   * This is useful when users have stale cached data (e.g., old CSP headers).
   */
  public async resetApp(): Promise<void> {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
      }
    }

    // Clear all caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
      }
    }

    // Reload the page to get fresh content
    window.location.reload();
  }
}

export type ServiceWorkerEvent =
  | 'sw_initialized'
  | 'update_available'
  | 'update_failed'
  | 'no_update_available'
  | 'sw_error'
  | 'update_active';
